import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { mergeExtensions } from '@/i18n/extensions';

// Translation data - All popular languages
// Base translations live in src/i18n/locales/<code>.js (one file per language).
import en from '@/i18n/locales/en';
import tr from '@/i18n/locales/tr';
import es from '@/i18n/locales/es';
import de from '@/i18n/locales/de';
import fr from '@/i18n/locales/fr';
import pt from '@/i18n/locales/pt';
import ru from '@/i18n/locales/ru';
import zh from '@/i18n/locales/zh';
import ja from '@/i18n/locales/ja';
import ko from '@/i18n/locales/ko';
import it from '@/i18n/locales/it';
import ar from '@/i18n/locales/ar';

const translations = { en, tr, es, de, fr, pt, ru, zh, ja, ko, it, ar };

// Merge per-feature translation files from src/i18n/extensions/*.js
mergeExtensions(translations);

const LanguageContext = createContext();

// =============================================================================
// LANGUAGE CONFIGURATION
// =============================================================================
// Production-enabled languages (shown in language selector)
// To enable more languages, add their codes here
// RTL languages (ar, he) should only be enabled after thorough testing
const ENABLED_LANGUAGES = process.env.REACT_APP_ENABLED_LANGUAGES 
  ? process.env.REACT_APP_ENABLED_LANGUAGES.split(',') 
  : ['en', 'tr', 'es'];

// Fallback language - NEVER show raw keys, always fall back to this
const FALLBACK_LANGUAGE = 'en';

// RTL languages - keep disabled until production is stable
const RTL_LANGUAGES = ['ar', 'he'];

// All available languages (including disabled ones for future use)
const ALL_LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'pt', label: 'Português', flag: '🇧🇷' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦', rtl: true },
];

// Get enabled languages for the language selector
const getEnabledLanguages = () => {
  return ALL_LANGUAGES.filter(lang => ENABLED_LANGUAGES.includes(lang.code));
};

export function LanguageProvider({ children }) {
  // Turkish is the default for NEW visitors; any saved enabled language
  // (including English) is respected.
  const [language, setLanguageState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('eam_language');
      if (saved && ENABLED_LANGUAGES.includes(saved)) {
        return saved;
      }
      localStorage.setItem('eam_language', 'tr');
    }
    return 'tr';
  });

  const setLanguage = useCallback((lang) => {
    // Only allow setting enabled languages
    if (!ENABLED_LANGUAGES.includes(lang)) {
      console.warn(`Language "${lang}" is not enabled. Using fallback.`);
      lang = FALLBACK_LANGUAGE;
    }

    setLanguageState(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem('eam_language', lang);
    }
  }, []);

  // Keep <html dir/lang> in sync with the active language
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isRTL = RTL_LANGUAGES.includes(language);
      document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
      document.documentElement.lang = language;
    }
  }, [language]);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'eam_language' && e.newValue) {
        // Only sync if the new language is enabled
        if (ENABLED_LANGUAGES.includes(e.newValue)) {
          setLanguageState(e.newValue);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Translation function with strict fallback - NEVER return raw key
  const t = useCallback((key, fallback) => {
    // Try current language first
    const currentTranslation = translations[language]?.[key];
    if (currentTranslation) return currentTranslation;

    // Fall back to English - this should ALWAYS have the key
    const fallbackTranslation = translations[FALLBACK_LANGUAGE]?.[key];
    if (fallbackTranslation) return fallbackTranslation;

    // If a fallback string was provided, use it
    if (fallback) return fallback;

    // If still not found, log warning in development and return fallback text
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Missing i18n key: "${key}" for languages: ${language}, ${FALLBACK_LANGUAGE}`);
    }
    // Return a user-friendly fallback instead of raw key
    return key.split('.').pop()?.replace(/([A-Z])/g, ' $1').trim() || key;
  }, [language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t,
    // Only expose enabled languages to the UI
    availableLanguages: getEnabledLanguages(),
    // Expose config for debugging/admin purposes
    config: {
      enabledLanguages: ENABLED_LANGUAGES,
      fallbackLanguage: FALLBACK_LANGUAGE,
      allLanguages: ALL_LANGUAGES,
      rtlLanguages: RTL_LANGUAGES,
    }
  }), [language, setLanguage, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export { translations };
